import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, FileText, Eye, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = ['pending', 'reviewed', 'interviewed', 'accepted', 'rejected'];

export const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', notes: '' });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Error fetching applications');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/applications/${selectedApp.id}`, editForm);
      toast.success('Application updated');
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      toast.error('Error updating application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-400';
      case 'reviewed': return 'bg-blue-500/10 text-blue-400';
      case 'interviewed': return 'bg-purple-500/10 text-purple-400';
      case 'accepted': return 'bg-green-500/10 text-green-400';
      case 'rejected': return 'bg-red-500/10 text-red-400';
      default: return 'bg-[#1A1A1A] text-[#808080]';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-applications">
        <div className="mb-8">
          <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
            Agent Applications
          </h1>
          <p className="text-[#808080] mt-1">Review and manage agent applications</p>
        </div>

        {/* Applications Table */}
        <div className="dashboard-card overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : applications.length === 0 ? (
            <p className="text-[#808080] text-center py-16">No applications yet</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4AF37]/10">
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Applicant</th>
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Contact</th>
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Experience</th>
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]/50">
                    <td className="py-3 px-4">
                      <p className="text-[#F5F5F0] font-medium">{app.name}</p>
                      {app.license_number && (
                        <p className="text-[#808080] text-xs">License: {app.license_number}</p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col space-y-1">
                        <a href={`mailto:${app.email}`} className="text-[#D4AF37] text-sm hover:underline flex items-center">
                          <Mail className="w-3 h-3 mr-1" /> {app.email}
                        </a>
                        <a href={`tel:${app.phone}`} className="text-[#808080] text-sm hover:text-[#D4AF37] flex items-center">
                          <Phone className="w-3 h-3 mr-1" /> {app.phone}
                        </a>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#F5F5F0]">{app.years_experience} years</p>
                      {app.current_brokerage && (
                        <p className="text-[#808080] text-xs">{app.current_brokerage}</p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded capitalize ${getStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#808080] text-sm">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <button 
                        onClick={() => {
                          setSelectedApp(app);
                          setEditForm({ status: app.status, notes: app.notes || '' });
                        }}
                        className="p-2 hover:bg-[#D4AF37]/10 rounded text-[#D4AF37]"
                        data-testid={`view-app-${app.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Modal */}
        {selectedApp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-[#111111] p-8 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  Application Details
                </h3>
                <button onClick={() => setSelectedApp(null)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Applicant</h4>
                  <p className="text-[#F5F5F0]">{selectedApp.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Email</h4>
                    <p className="text-[#F5F5F0] text-sm">{selectedApp.email}</p>
                  </div>
                  <div>
                    <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Phone</h4>
                    <p className="text-[#F5F5F0] text-sm">{selectedApp.phone}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Experience</h4>
                    <p className="text-[#F5F5F0] text-sm">{selectedApp.years_experience} years</p>
                  </div>
                  <div>
                    <h4 className="text-[#D4AF37] text-sm font-medium mb-1">License</h4>
                    <p className="text-[#F5F5F0] text-sm">{selectedApp.license_number || 'N/A'}</p>
                  </div>
                </div>
                {selectedApp.current_brokerage && (
                  <div>
                    <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Current Brokerage</h4>
                    <p className="text-[#F5F5F0] text-sm">{selectedApp.current_brokerage}</p>
                  </div>
                )}
                <div>
                  <h4 className="text-[#D4AF37] text-sm font-medium mb-1">Why They Want to Join</h4>
                  <p className="text-[#F5F5F0] text-sm">{selectedApp.why_join || 'Not provided'}</p>
                </div>

                <div className="pt-4 border-t border-[#D4AF37]/10">
                  <div className="mb-4">
                    <label className="text-[#808080] text-sm mb-2 block">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0]"
                      data-testid="app-status-select"
                    >
                      {statusOptions.map(s => (
                        <option key={s} value={s} className="capitalize">{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="text-[#808080] text-sm mb-2 block">Notes</label>
                    <Textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                      placeholder="Add notes..."
                      data-testid="app-notes-input"
                    />
                  </div>
                  <Button onClick={handleUpdate} className="btn-gold w-full" data-testid="update-app-btn">
                    Update Application
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
